import React, { useRef, useEffect, useState } from 'react';
import './VideoCard.css';
import ShareDialog from './ShareDialog';
import { useInstitution } from '../contexts/InstitutionContext';

const BACKEND_URL = 'https://auth-coaching-dashboard-386051205660.us-central1.run.app';

function VideoCard({ snippet, isActive }) {
  const iframeRef = useRef(null);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const { institutionId } = useInstitution();

  useEffect(() => {
    if (!iframeRef.current) return;

    const iframe = iframeRef.current;

    const initPlayer = async () => {
      const player = new window.Vimeo.Player(iframe);

      if (isActive) {
        await player.setVolume(1).catch(() => {});
        await player.play().catch(() => {});
      } else {
        await player.pause().catch(() => {});
        const timeParam = parseFloat(snippet.timestamp_start);
        await player.setCurrentTime(timeParam).catch(() => {});
      }
    };

    const timer = setTimeout(() => {
      initPlayer();
    }, 100);

    return () => clearTimeout(timer);
  }, [isActive, snippet.timestamp_start]);

  const handleShareSnippet = () => {
    // Use the backend preview endpoint for rich meta tags (thumbnails, etc.)
    const url = `${BACKEND_URL}/snippets/preview/${institutionId}/${snippet.uid}`;
    setShareUrl(url);
    setShowShareDialog(true);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getVimeoVideoId = (url) => {
    if (!url) return null;
    const match = url.match(/vimeo\.com\/(\d+)/);
    return match ? match[1] : null;
  };

  const videoId = getVimeoVideoId(snippet.streamingUrl);
  const timeParam = Math.floor(parseFloat(snippet.timestamp_start));
  const endTimeParam = Math.floor(parseFloat(snippet.timestamp_end));

  return (
    <>
      <div className="video-card">
        <div className="video-container">
          {videoId && (
            <iframe
              ref={iframeRef}
              key={`${videoId}-${timeParam}`}
              src={`https://player.vimeo.com/video/${videoId}?muted=0&autoplay=0&loop=1&&watch_full_video=0#t=${timeParam}s&end=${endTimeParam}`}
              width="100%"
              height="100%"
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              title={`Video ${videoId}`}
            />
          )}
        </div>

        <div className="video-info">
          <div className="info-header">
            <div className="category-badge">{snippet.category}</div>
            <button className="snippet-share-button" onClick={handleShareSnippet} title="Share snippet">
              <img src="/share.png" alt="Share" className="share-icon" />
              <span className="share-text">Share</span>
            </button>
          </div>

          <div className="timestamp">
            {formatTime(snippet.timestamp_start)} - {formatTime(snippet.timestamp_end)}
          </div>

          <div className="rationale">
            <p>{snippet.parent_facing_rationale}</p>
          </div>

          <div className="hashtags">
            {snippet.key_moment_tags.map((tag, index) => (
              <span key={index} className="hashtag">{tag}</span>
            ))}
          </div>
        </div>
      </div>

      <ShareDialog
        isOpen={showShareDialog}
        onClose={() => setShowShareDialog(false)}
        shareUrl={shareUrl}
        snippet={snippet}
      />
    </>
  );
}

export default VideoCard;
